import { memo } from "react";
import { HistoryField } from "@common/constants/history-source-types";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { AccountCard } from "@components/financialAccount/AccountCard/AccountCard";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useEntitiesById } from "@hooks/useEntities";
import { EntityDisplay } from "@components/entity/EntityDisplay";
import { DocumentCategoryDisplay } from "@components/documents/DocumentCategoryDisplay";
import { DocumentName } from "@components/documents/DocumentName";
import { EmptyValue } from "@design-system/EmptyValue/EmptyValue";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { HistoryDocumentUpdateDetailsRow } from "../utils/historyDocumentDetailsRow";

interface Props {
    row: { original: HistoryDocumentUpdateDetailsRow };
    side: "previous" | "modified";
}

const RAW_DASH = "-";

const HistoryDocumentDiffValueCell = memo(({ row, side }: Props) => {
    const accountId =
        side === "previous"
            ? row.original.previousAccountId
            : row.original.modifiedAccountId;
    const entityId =
        side === "previous"
            ? row.original.previousEntityId
            : row.original.modifiedEntityId;
    const categoryId =
        side === "previous"
            ? row.original.previousCategoryId
            : row.original.modifiedCategoryId;
    const text =
        side === "previous" ? row.original.previous : row.original.modified;

    const accounts = useFinancialAccounts();
    const allEntities = useEntitiesById();

    if (accountId !== null) {
        const found = accounts?.find((account) => account.id === accountId);
        if (!found) {
            return (
                <ViewTableCell>
                    <BreakableText text={text} showTooltip />
                </ViewTableCell>
            );
        }
        return (
            <ViewTableCell>
                <AccountCard account={found} showType={false} iconSize="sm" />
            </ViewTableCell>
        );
    }

    if (entityId !== null) {
        const entity = allEntities.get(entityId);
        if (!entity) {
            return (
                <ViewTableCell>
                    <EmptyValue />
                </ViewTableCell>
            );
        }
        return (
            <ViewTableCell>
                <EntityDisplay entity={entity} />
            </ViewTableCell>
        );
    }

    if (categoryId !== null) {
        return (
            <ViewTableCell>
                <DocumentCategoryDisplay documentCategoryId={categoryId} />
            </ViewTableCell>
        );
    }

    if (text === RAW_DASH) {
        return (
            <ViewTableCell>
                <EmptyValue />
            </ViewTableCell>
        );
    }

    if (row.original.changedField === HistoryField.FILE_NAME) {
        return (
            <ViewTableCell>
                <DocumentName
                    fileName={text}
                    documentId={row.original.resourceId}
                />
            </ViewTableCell>
        );
    }

    return <ViewTableCell>{text}</ViewTableCell>;
});

HistoryDocumentDiffValueCell.displayName = "HistoryDocumentDiffValueCell";

export const HistoryDocumentPreviousValueCell = memo(
    (props: { row: { original: HistoryDocumentUpdateDetailsRow } }) => (
        <HistoryDocumentDiffValueCell {...props} side="previous" />
    ),
);
HistoryDocumentPreviousValueCell.displayName =
    "HistoryDocumentPreviousValueCell";

export const HistoryDocumentModifiedValueCell = memo(
    (props: { row: { original: HistoryDocumentUpdateDetailsRow } }) => (
        <HistoryDocumentDiffValueCell {...props} side="modified" />
    ),
);
HistoryDocumentModifiedValueCell.displayName =
    "HistoryDocumentModifiedValueCell";
